import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useCustomDialog } from '../context/DialogContext';

const QRCodeModal = ({ onClose, upiId, payeeName, amount, order, restaurantPhone }) => {
    const { alert: customAlert } = useCustomDialog();
    const [isCopied, setIsCopied] = useState(false);
    const [upiDigits, setUpiDigits] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showUpiInput, setShowUpiInput] = useState(false);

    if (!upiId) {
        return (
            <div id="qr-code-modal" className="modal-overlay visible" style={{ display: 'flex' }} onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close-btn" onClick={onClose}>&times;</button>
                    <h3>Payment Details Unavailable</h3>
                    <p>This restaurant has not configured their payment details yet.</p>
                </div>
            </div>
        );
    }
    
    const upiLink = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(payeeName)}&cu=INR${amount ? `&am=${amount}` : ''}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(upiId).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            customAlert('Failed to copy UPI ID.', 'Copy Error', 'danger');
        });
    };

    const handleShareScreenshot = () => {
        if (!restaurantPhone) {
            customAlert('Restaurant phone number not available.', 'Error', 'danger');
            return;
        }
        let msg = `Here is the payment screenshot for my order.\n`;
        if (order && order.order_no) {
            msg = `Here is the payment screenshot for Order ${order.order_no} (Total: ₹${amount})`;
        }
        const encodedText = encodeURIComponent(msg);
        window.open(`https://wa.me/${restaurantPhone}?text=${encodedText}`, '_blank');
    };

    const handleSubmitUpi = async () => {
        if (upiDigits.length !== 4) {
            customAlert('Please enter exactly 4 digits.', 'Validation', 'danger');
            return;
        }
        if (!order || !order.id) {
            customAlert('Order context lost. Please try again.', 'Error', 'danger');
            return;
        }
        
        setSubmitting(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/verify_order_payment.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order_id: order.id,
                    payment_txn_id: upiDigits,
                    amount_paid: amount
                })
            });
            const data = await res.json();
            if (data.status === 'success') {
                customAlert('Payment verification submitted! Kitchen will start preparing soon.', 'Success', 'success');
                onClose();
            } else {
                customAlert(data.message || 'Failed to submit', 'Error', 'danger');
            }
        } catch (err) {
            console.error(err);
            customAlert('Network error', 'Error', 'danger');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div id="qr-code-modal" className="modal-overlay visible" style={{ display: 'flex' }} onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>&times;</button>
                <h3>Pay Your Bill to {payeeName}</h3>
                <p>You can scan the code or pay directly via app.</p>
                
                <div style={{ margin: '15px auto', padding: '10px', backgroundColor: 'white', display: 'inline-block' }}>
                    <QRCodeSVG
                        value={upiLink}
                        size={220}
                        level={"H"}
                        includeMargin={true}
                    />
                </div>

                <a id="upi-payment-link" href={upiLink} className="btn" style={{ marginBottom: '15px' }}>
                    <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
                    {amount ? `Pay ₹${amount} via App` : 'Pay via App'}
                </a>
                
                <div className="upi-id-container" style={{ marginBottom: '20px' }}>
                    <p>Or copy UPI ID:</p>
                    <div className="upi-id-box">
                        <strong id="upi-id-text">{upiId}</strong>
                        <button id="copy-upi-btn" title="Copy UPI ID" onClick={handleCopy} style={{ backgroundColor: isCopied ? '#1a9c4b' : '' }}>
                            {isCopied ? <i className="fas fa-check"></i> : <i className="far fa-copy"></i>}
                        </button>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <button 
                        onClick={handleShareScreenshot}
                        style={{ background: '#25D366', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                    >
                        <i className="fab fa-whatsapp"></i> Share Screenshot
                    </button>

                    {!showUpiInput ? (
                        <button 
                            onClick={() => setShowUpiInput(true)}
                            style={{ background: 'var(--primary-orange)', color: '#fff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                        >
                            <i className="fas fa-check-circle"></i> I Have Paid
                        </button>
                    ) : (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input 
                                type="text" 
                                placeholder="Last 4 digits of UPI TXN ID" 
                                value={upiDigits}
                                onChange={(e) => setUpiDigits(e.target.value.replace(/\D/g, '').substring(0, 4))}
                                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', textAlign: 'center', letterSpacing: '2px', fontSize: '1.1rem' }}
                            />
                            <button 
                                onClick={handleSubmitUpi}
                                disabled={submitting || upiDigits.length !== 4}
                                style={{ background: 'var(--green-accent)', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: upiDigits.length === 4 ? 'pointer' : 'not-allowed', opacity: upiDigits.length === 4 ? 1 : 0.6 }}
                            >
                                {submitting ? '...' : 'Submit'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QRCodeModal;